import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNotePlanOfCareCompact.css';

/**
 * Delay between the clinician's final edit and autosave.
 */
const AUTOSAVE_DELAY_MS = 900;

/**
 * Resolve ClinicalNoteMain.entityVersion from a note response.
 */
function resolveEntityVersion(response) {
  if (!response) {
    return null;
  }

  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}

/**
 * Convert nullable backend values to controlled React values.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }

  return String(value);
}

/**
 * Normalize one PlanOfCareResponse for frontend editing.
 */
function normalizePlanOfCare(response) {
  return {
    ...response,

    draftCertificationText:
      toInputValue(
        response?.certificationText
      ),

    draftReferringProviderName:
      toInputValue(
        response?.referringProviderName
      ),

    /*
     * Provider ID is preserved from the backend but is not manually
     * editable here. A provider-selector integration can replace this
     * later without changing the Plan of Care save contract.
     */
    draftReferringProviderId:
      response?.referringProviderId ??
      null,

    draftCertificationStartDate:
      toInputValue(
        response?.certificationStartDate
      ),

    draftCertificationEndDate:
      toInputValue(
        response?.certificationEndDate
      ),

    draftFrequencyText:
      toInputValue(
        response?.frequencyText
      ),

    draftDurationText:
      toInputValue(
        response?.durationText
      ),

    draftPlanText:
      toInputValue(
        response?.planText
      ),

    draftPhysicianSignatureName:
      toInputValue(
        response?.physicianSignatureName
      ),

    draftPhysicianSignatureDate:
      toInputValue(
        response?.physicianSignatureDate
      ),
  };
}

/**
 * Capture the last successfully saved backend state.
 */
function createBaseline(planOfCare) {
  return {
    certificationText:
      toInputValue(
        planOfCare?.certificationText
      ),

    referringProviderName:
      toInputValue(
        planOfCare?.referringProviderName
      ),

    referringProviderId:
      planOfCare?.referringProviderId ??
      null,

    certificationStartDate:
      toInputValue(
        planOfCare?.certificationStartDate
      ),

    certificationEndDate:
      toInputValue(
        planOfCare?.certificationEndDate
      ),

    frequencyText:
      toInputValue(
        planOfCare?.frequencyText
      ),

    durationText:
      toInputValue(
        planOfCare?.durationText
      ),

    planText:
      toInputValue(
        planOfCare?.planText
      ),

    physicianSignatureName:
      toInputValue(
        planOfCare?.physicianSignatureName
      ),

    physicianSignatureDate:
      toInputValue(
        planOfCare?.physicianSignatureDate
      ),
  };
}

/**
 * Determine whether the current editable draft differs from the last
 * successfully saved backend state.
 */
function hasDraftChanges(
  planOfCare,
  baseline
) {
  if (!planOfCare) {
    return false;
  }

  return (
    planOfCare
      .draftCertificationText !==
      baseline.certificationText ||

    planOfCare
      .draftReferringProviderName !==
      baseline.referringProviderName ||

    planOfCare
      .draftReferringProviderId !==
      baseline.referringProviderId ||

    planOfCare
      .draftCertificationStartDate !==
      baseline.certificationStartDate ||

    planOfCare
      .draftCertificationEndDate !==
      baseline.certificationEndDate ||

    planOfCare
      .draftFrequencyText !==
      baseline.frequencyText ||

    planOfCare
      .draftDurationText !==
      baseline.durationText ||

    planOfCare
      .draftPlanText !==
      baseline.planText ||

    planOfCare
      .draftPhysicianSignatureName !==
      baseline.physicianSignatureName ||

    planOfCare
      .draftPhysicianSignatureDate !==
      baseline.physicianSignatureDate
  );
}

/**
 * Basic structural validation that applies even during draft
 * autosave.
 */
function validateDraft(planOfCare) {
  const errors = {};

  const startDate =
    planOfCare
      ?.draftCertificationStartDate;

  const endDate =
    planOfCare
      ?.draftCertificationEndDate;

  if (
    startDate &&
    endDate &&
    endDate < startDate
  ) {
    errors.certificationEndDate =
      'Certification end date cannot be before the certification start date.';
  }

  return errors;
}

/**
 * Validate all fields required when the clinician explicitly requests
 * completed=true.
 *
 * Spring Boot remains authoritative and performs the same completion
 * validation again.
 */
function validateCompletion(
  planOfCare
) {
  const errors =
    validateDraft(
      planOfCare
    );

  if (
    !planOfCare
      ?.draftCertificationText
      ?.trim()
  ) {
    errors.certificationText =
      'Certification text is required.';
  }

  if (
    !planOfCare
      ?.draftReferringProviderName
      ?.trim()
  ) {
    errors.referringProviderName =
      'Referring provider name is required.';
  }

  if (
    !planOfCare
      ?.draftCertificationStartDate
  ) {
    errors.certificationStartDate =
      'Certification start date is required.';
  }

  if (
    !planOfCare
      ?.draftCertificationEndDate
  ) {
    errors.certificationEndDate =
      errors.certificationEndDate ||
      'Certification end date is required.';
  }

  if (
    !planOfCare
      ?.draftFrequencyText
      ?.trim()
  ) {
    errors.frequencyText =
      'Treatment frequency is required.';
  }

  if (
    !planOfCare
      ?.draftDurationText
      ?.trim()
  ) {
    errors.durationText =
      'Treatment duration is required.';
  }

  if (
    !planOfCare
      ?.draftPlanText
      ?.trim()
  ) {
    errors.planText =
      'Plan of Care narrative is required.';
  }

  if (
    planOfCare
      ?.physicianSignatureRequired
  ) {
    if (
      !planOfCare
        ?.draftPhysicianSignatureName
        ?.trim()
    ) {
      errors.physicianSignatureName =
        'Referring physician certification name is required.';
    }

    if (
      !planOfCare
        ?.draftPhysicianSignatureDate
    ) {
      errors.physicianSignatureDate =
        'Referring physician certification date is required.';
    }
  }

  return errors;
}

/**
 * Reusable text input.
 */
function TextField({
  id,
  label,
  value,
  disabled,
  required = false,
  error,
  placeholder,
  onChange,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 5,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {label}

        {required && (
          <span
            aria-hidden="true"
            style={{
              marginLeft: 4,
              color: '#b42318',
            }}
          >
            *
          </span>
        )}
      </label>

      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        aria-invalid={
          Boolean(error)
        }
        style={{
          width: '100%',
          boxSizing:
            'border-box',
          minHeight: 38,
          border:
            error
              ? '1px solid #b42318'
              : '1px solid #bbb',
          borderRadius: 6,
          padding:
            '7px 9px',
          fontSize: 14,
          background:
            disabled
              ? '#f3f4f6'
              : '#fff',
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Reusable date input.
 */
function DateField({
  id,
  label,
  value,
  disabled,
  required = false,
  error,
  onChange,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 5,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {label}

        {required && (
          <span
            aria-hidden="true"
            style={{
              marginLeft: 4,
              color: '#b42318',
            }}
          >
            *
          </span>
        )}
      </label>

      <input
        id={id}
        type="date"
        value={value}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        aria-invalid={
          Boolean(error)
        }
        style={{
          width: '100%',
          boxSizing:
            'border-box',
          minHeight: 38,
          border:
            error
              ? '1px solid #b42318'
              : '1px solid #bbb',
          borderRadius: 6,
          padding:
            '7px 9px',
          fontSize: 14,
          background:
            disabled
              ? '#f3f4f6'
              : '#fff',
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Reusable textarea.
 */
function TextAreaField({
  id,
  label,
  value,
  disabled,
  required = false,
  error,
  placeholder,
  rows = 4,
  onChange,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          marginBottom: 5,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {label}

        {required && (
          <span
            aria-hidden="true"
            style={{
              marginLeft: 4,
              color: '#b42318',
            }}
          >
            *
          </span>
        )}
      </label>

      <textarea
        id={id}
        rows={rows}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        aria-invalid={
          Boolean(error)
        }
        style={{
          width: '100%',
          boxSizing:
            'border-box',
          resize:
            disabled
              ? 'none'
              : 'vertical',
          border:
            error
              ? '1px solid #b42318'
              : '1px solid #bbb',
          borderRadius: 6,
          padding: 10,
          fontSize: 14,
          lineHeight: 1.5,
          background:
            disabled
              ? '#f3f4f6'
              : '#fff',
        }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Clinical-note Plan of Care editor.
 *
 * Features:
 *
 * - payer-derived Plan of Care requirement
 * - unsaved backend preview support
 * - certification statement
 * - referring provider information
 * - certification period
 * - treatment frequency and duration
 * - Plan of Care narrative
 * - separate physician certification
 * - 900ms draft autosave
 * - explicit Complete Plan of Care action
 * - optimistic-lock version refresh
 */
export default function ClinicalNotePlanOfCareSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [
    planOfCare,
    setPlanOfCare,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    loadError,
    setLoadError,
  ] = useState('');

  const [
    saveError,
    setSaveError,
  ] = useState('');

  const [
    saveStatus,
    setSaveStatus,
  ] = useState('saved');

  const [
    editTick,
    setEditTick,
  ] = useState(0);

  const [
    initialized,
    setInitialized,
  ] = useState(false);

  const [
    completionAttempted,
    setCompletionAttempted,
  ] = useState(false);

  // Presentation state only. Draft values stay in the parent editor when
  // panels collapse, and completion errors reopen the relevant panel.
  const [showCertificationStatement, setShowCertificationStatement] = useState(false);
  const [showPlanNarrative, setShowPlanNarrative] = useState(false);
  const [showPhysicianCertification, setShowPhysicianCertification] = useState(false);


  /*
   * Mutable autosave state.
   */
  const planOfCareRef =
    useRef(null);

  const baselineRef =
    useRef(
      createBaseline(
        null
      )
    );

  const versionRef =
    useRef(null);

  const revisionRef =
    useRef(0);

  const dirtyRef =
    useRef(false);

  const savingRef =
    useRef(false);

  const failedRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  const flushSaveRef =
    useRef(null);

  const lastSaveWasCompletionRef =
    useRef(false);

  // The coordinator needs synchronous state, not delayed React renders.
  const initializedRef = useRef(false);
  const loadingRef = useRef(true);
  const loadErrorRef = useRef('');
  const saveErrorRef = useRef('');
  const inFlightSaveRef = useRef(null);

  useEffect(() => {
    planOfCareRef.current =
      planOfCare;
  }, [planOfCare]);

  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /**
   * Load Plan of Care preview/saved data and current note version.
   */
  useEffect(() => {
    let cancelled = false;

    async function loadPlanOfCare() {
      loadingRef.current = true;
      initializedRef.current = false;
      loadErrorRef.current = '';
      saveErrorRef.current = '';
      setLoading(true);
      setLoadError('');
      setSaveError('');
      setInitialized(false);
      setCompletionAttempted(false);
      setShowCertificationStatement(false);
      setShowPlanNarrative(false);
      setShowPhysicianCertification(false);

      dirtyRef.current =
        false;

      failedRef.current =
        false;

      savingRef.current =
        false;

      revisionRef.current =
        0;

      try {
        const [
          planResponse,
          noteResponse,
        ] =
          await Promise.all([
            clinicalNoteService
              .getPlanOfCare(
                clinicalNoteId
              ),

            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),
          ]);

        if (cancelled) {
          return;
        }

        const version =
          resolveEntityVersion(
            noteResponse
          );

        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }

        if (
          !planResponse ||
          typeof planResponse !==
            'object'
        ) {
          throw new Error(
            'Plan of Care configuration could not be loaded.'
          );
        }

        const normalized =
          normalizePlanOfCare(
            planResponse
          );

        versionRef.current =
          version;

        planOfCareRef.current =
          normalized;

        baselineRef.current =
          createBaseline(
            planResponse
          );

        setPlanOfCare(
          normalized
        );

        if (
          normalized.completed
        ) {
          setSaveStatus(
            'completed'
          );
        } else if (
          !normalized.planOfCareId
        ) {
          setSaveStatus(
            'preview'
          );
        } else {
          setSaveStatus(
            'saved'
          );
        }

        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }

        loadErrorRef.current = error?.message || 'Plan of Care could not be loaded.';
        setLoadError(loadErrorRef.current);
      } finally {
        if (!cancelled) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    }

    if (clinicalNoteId) {
      loadPlanOfCare();
    } else {
      loadErrorRef.current = 'Clinical note ID is required.';
      loadingRef.current = false;
      setLoadError(loadErrorRef.current);
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);

  /**
   * Refresh ClinicalNoteMain.entityVersion after a child save.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );

        const newVersion =
          resolveEntityVersion(
            noteResponse
          );

        if (
          newVersion === null ||
          newVersion === undefined
        ) {
          throw new Error(
            'The Plan of Care was saved, but the updated clinical note version could not be loaded.'
          );
        }

        versionRef.current =
          newVersion;

        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }

        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );

  /**
   * Update one Plan of Care draft field.
   *
   * Editing a completed Plan of Care makes the local draft incomplete.
   * The following autosave persists completed=false before it can be
   * explicitly completed again.
   */
  function updateField(
    field,
    value
  ) {
    if (
      disabled ||
      !planOfCareRef.current
    ) {
      return;
    }

    const previousPlan = planOfCareRef.current;
    const nextPlan = {
      ...previousPlan,

      [field]:
        value,

      completed:
        false,

      certificationComplete:
        false,
    };

    planOfCareRef.current =
      nextPlan;

    setPlanOfCare(
      nextPlan
    );

    revisionRef.current +=
      1;

    failedRef.current =
      false;

    dirtyRef.current =
      hasDraftChanges(
        nextPlan,
        baselineRef.current
      ) ||
      Boolean(previousPlan.completed);

    saveErrorRef.current = '';
    setSaveError('');

    setSaveStatus(
      'unsaved'
    );

    setEditTick(
      (current) =>
        current + 1
    );
  }

  /**
   * Build the exact SavePlanOfCareRequest payload.
   */
  function buildSaveRequest(
    currentPlan,
    completed
  ) {
    return {
      expectedVersion:
        versionRef.current,

      certificationText:
        currentPlan
          .draftCertificationText,

      referringProviderName:
        currentPlan
          .draftReferringProviderName,

      /*
       * Preserve an existing provider record association.
       *
       * Provider selection itself is not invented by this component.
       */
      referringProviderId:
        currentPlan
          .draftReferringProviderId,

      certificationStartDate:
        currentPlan
          .draftCertificationStartDate,

      certificationEndDate:
        currentPlan
          .draftCertificationEndDate,

      frequencyText:
        currentPlan
          .draftFrequencyText,

      durationText:
        currentPlan
          .draftDurationText,

      planText:
        currentPlan
          .draftPlanText,

      physicianSignatureName:
        currentPlan
          .draftPhysicianSignatureName,

      physicianSignatureDate:
        currentPlan
          .draftPhysicianSignatureDate,

      completed,
    };
  }

  /**
   * Apply a successful backend response.
   */
  function applySuccessfulSave(
    response,
    revisionBeingSaved
  ) {
    const normalizedResponse =
      normalizePlanOfCare(
        response
      );

    const newBaseline =
      createBaseline(
        response
      );

    baselineRef.current =
      newBaseline;

    const currentRevision =
      revisionRef.current;

    if (
      currentRevision ===
      revisionBeingSaved
    ) {
      planOfCareRef.current =
        normalizedResponse;

      setPlanOfCare(
        normalizedResponse
      );

      dirtyRef.current =
        false;

      setSaveStatus(
        normalizedResponse.completed
          ? 'completed'
          : 'saved'
      );

      return;
    }

    /*
     * The clinician edited again while the request was in flight.
     *
     * Refresh backend-derived payer information but preserve the newer
     * local values. Because these newer edits are not represented by
     * the successful response, the local Plan of Care is no longer
     * considered completed.
     */
    const currentDraft =
      planOfCareRef.current;

    const merged = {
      ...normalizedResponse,

      draftCertificationText:
        currentDraft
          ?.draftCertificationText ??
        normalizedResponse
          .draftCertificationText,

      draftReferringProviderName:
        currentDraft
          ?.draftReferringProviderName ??
        normalizedResponse
          .draftReferringProviderName,

      draftReferringProviderId:
        currentDraft
          ?.draftReferringProviderId ??
        normalizedResponse
          .draftReferringProviderId,

      draftCertificationStartDate:
        currentDraft
          ?.draftCertificationStartDate ??
        normalizedResponse
          .draftCertificationStartDate,

      draftCertificationEndDate:
        currentDraft
          ?.draftCertificationEndDate ??
        normalizedResponse
          .draftCertificationEndDate,

      draftFrequencyText:
        currentDraft
          ?.draftFrequencyText ??
        normalizedResponse
          .draftFrequencyText,

      draftDurationText:
        currentDraft
          ?.draftDurationText ??
        normalizedResponse
          .draftDurationText,

      draftPlanText:
        currentDraft
          ?.draftPlanText ??
        normalizedResponse
          .draftPlanText,

      draftPhysicianSignatureName:
        currentDraft
          ?.draftPhysicianSignatureName ??
        normalizedResponse
          .draftPhysicianSignatureName,

      draftPhysicianSignatureDate:
        currentDraft
          ?.draftPhysicianSignatureDate ??
        normalizedResponse
          .draftPhysicianSignatureDate,

      completed:
        false,

      certificationComplete:
        false,
    };

    planOfCareRef.current =
      merged;

    setPlanOfCare(
      merged
    );

    dirtyRef.current =
      true;

    setSaveStatus(
      'unsaved'
    );
  }

  /**
   * Save Plan of Care.
   *
   * completed=false:
   * ordinary draft autosave
   *
   * completed=true:
   * explicit clinician completion action
   */
  const savePlanOfCare =
    useCallback(
      async ({
        completed = false,
        force = false,
      } = {}) => {
        if (
          disabled ||
          !initialized ||
          savingRef.current ||
          !planOfCareRef.current
        ) {
          return;
        }

        const currentPlan =
          planOfCareRef.current;

        const errors =
          completed
            ? validateCompletion(
                currentPlan
              )
            : validateDraft(
                currentPlan
              );

        if (
          Object.keys(
            errors
          ).length > 0
        ) {
          setSaveStatus(
            'invalid'
          );

          if (completed) {
            setCompletionAttempted(
              true
            );
          }

          return;
        }

        if (
          !force &&
          !completed &&
          !dirtyRef.current
        ) {
          return;
        }

        savingRef.current =
          true;

        failedRef.current =
          false;

        lastSaveWasCompletionRef.current =
          completed;

        saveErrorRef.current = '';
        setSaveError('');

        setSaveStatus(
          'saving'
        );

        const revisionBeingSaved =
          revisionRef.current;

        try {
          // Other sections may have saved since this editor last loaded.
          // Always fetch the latest parent version before this mutation;
          // Spring Boot still rejects genuinely concurrent conflicting writes.
          await refreshEntityVersion();

          const response =
            await clinicalNoteService
              .savePlanOfCare(
                clinicalNoteId,
                buildSaveRequest(
                  currentPlan,
                  completed
                )
              );

          if (
            !mountedRef.current
          ) {
            return;
          }

          const noteResponse =
            await refreshEntityVersion();

          if (
            !mountedRef.current
          ) {
            return;
          }

          saveErrorRef.current = '';
          applySuccessfulSave(
            response,
            revisionBeingSaved
          );

          if (completed) {
            setCompletionAttempted(
              false
            );
          }

          if (
            typeof onSaved ===
            'function'
          ) {
            onSaved(
              response,
              noteResponse
            );
          }
        } catch (error) {
          if (
            !mountedRef.current
          ) {
            return;
          }

          failedRef.current =
            true;

          setSaveStatus(
            'error'
          );

          saveErrorRef.current = error?.message || 'Plan of Care could not be saved.';
          setSaveError(saveErrorRef.current);
        } finally {
          savingRef.current =
            false;

          /*
           * If a newer edit happened during the request, persist the
           * newer draft as completed=false using the refreshed version.
           */
          if (
            mountedRef.current &&
            dirtyRef.current &&
            !failedRef.current
          ) {
            window.setTimeout(
              () => {
                flushSaveRef
                  .current?.({
                    completed: false,
                    force: false,
                  });
              },
              150
            );
          }
        }
      },
      [
        clinicalNoteId,
        disabled,
        initialized,
        onSaved,
        refreshEntityVersion,
      ]
    );

  // Every caller gets the same awaitable request while a save is in flight.
  // In particular, navigating away cannot race the normal 900ms autosave.
  useEffect(() => {
    flushSaveRef.current = (options = {}) => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const work = Promise.resolve().then(() => savePlanOfCare(options));
      const tracked = work.finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [savePlanOfCare]);

  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    const unregister = navigationCoordinator.register(
      clinicalNoteId,
      'plan-of-care',
      {
        flush: async () => {
          // A completion is ONLY initiated by the user button; navigation
          // never sets completed=true or forces an unchanged preview to save.
          for (let attempt = 0; attempt < 100; attempt += 1) {
            const existing = inFlightSaveRef.current;
            if (existing) {
              await existing;
            } else if (initializedRef.current && dirtyRef.current &&
                       !failedRef.current && !disabled) {
              await flushSaveRef.current?.({ completed: false, force: false });
            } else {
              break;
            }
            if (!inFlightSaveRef.current &&
                (!dirtyRef.current || failedRef.current)) break;
          }
        },
        getState: () => {
          const pending = dirtyRef.current;
          const invalid = pending && Boolean(
            planOfCareRef.current &&
            Object.keys(validateDraft(planOfCareRef.current)).length
          );
          return {
            saving: savingRef.current || Boolean(inFlightSaveRef.current),
            dirty: pending,
            invalid,
            error: loadErrorRef.current || saveErrorRef.current ||
              (loadingRef.current || !initializedRef.current
                ? 'Plan of Care has not finished loading.' : ''),
          };
        },
      }
    );
    return unregister;
  }, [navigationCoordinator, clinicalNoteId, disabled]);

  /**
   * 900ms draft autosave.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      !dirtyRef.current ||
      failedRef.current
    ) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.({
              completed: false,
              force: false,
            });
        },
        AUTOSAVE_DELAY_MS
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editTick,
    initialized,
    disabled,
  ]);

  /**
   * Explicitly complete the Plan of Care.
   */
  function completePlanOfCare() {
    if (disabled) {
      return;
    }

    setCompletionAttempted(
      true
    );

    flushSaveRef
      .current?.({
        completed: true,
        force: true,
      });
  }

  /**
   * Retry the most recent failed request.
   */
  function retrySave() {
    if (disabled) {
      return;
    }

    failedRef.current =
      false;

    saveErrorRef.current = '';
    setSaveError('');

    flushSaveRef
      .current?.({
        completed:
          lastSaveWasCompletionRef
            .current,

        force: true,
      });
  }

  const draftErrors =
    useMemo(
      () =>
        planOfCare
          ? validateDraft(
              planOfCare
            )
          : {},
      [planOfCare]
    );

  const completionErrors =
    useMemo(
      () =>
        planOfCare
          ? validateCompletion(
              planOfCare
            )
          : {},
      [planOfCare]
    );

  /**
   * Only expose completion-field errors after the clinician actually
   * attempts completion.
   *
   * Structural draft errors such as reversed dates always display.
   */
  function fieldError(
    field
  ) {
    return (
      draftErrors[field] ||
      (
        completionAttempted
          ? completionErrors[
              field
            ]
          : ''
      ) ||
      ''
    );
  }

  const isPreview =
    Boolean(
      planOfCare &&
      !planOfCare.planOfCareId
    );

  const isCompleted =
    Boolean(
      planOfCare?.completed
    );

  const incompleteFieldCount =
    useMemo(
      () =>
        Object.keys(
          completionErrors
        ).length,
      [completionErrors]
    );

  let saveStatusText =
    disabled
      ? 'Read only'
      : 'Saved';

  switch (saveStatus) {
    case 'preview':
      saveStatusText =
        'Not yet saved';
      break;

    case 'unsaved':
      saveStatusText =
        'Unsaved changes';
      break;

    case 'saving':
      saveStatusText =
        'Saving…';
      break;

    case 'invalid':
      saveStatusText =
        'Needs correction';
      break;

    case 'error':
      saveStatusText =
        'Autosave failed';
      break;

    case 'completed':
      saveStatusText =
        'Completed';
      break;

    default:
      break;
  }

  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading Plan of Care…
      </section>
    );
  }

  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Plan of Care section could
          not be loaded
        </strong>

        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }

  if (!planOfCare) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems:
            'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 17,
              }}
            >
              Plan of Care
            </h3>

            {planOfCare
              .planOfCareRequired && (
              <span
                style={{
                  borderRadius: 999,
                  padding: '2px 7px',
                  background: isCompleted
                    ? '#e8f5e9'
                    : '#fce8e6',
                  color: isCompleted
                    ? '#1f6b2a'
                    : '#8a1c13',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {isCompleted
                  ? 'Required ✓'
                  : 'Required'}
              </span>
            )}

            {!planOfCare
              .planOfCareRequired && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#eef3f8',
                  color: '#334e68',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Not required by payer
              </span>
            )}

            {planOfCare
              .showPlanOfCareOnPdf && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#e8f4fd',
                  color: '#205493',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Included on PDF
              </span>
            )}

            {isPreview && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#fff3cd',
                  color: '#795500',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Preview
              </span>
            )}

            {isCompleted && (
              <span
                style={{
                  borderRadius: 999,
                  padding:
                    '2px 7px',
                  background:
                    '#e7f6ea',
                  color: '#276738',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                Completed
              </span>
            )}
          </div>

          <div
            style={{
              marginTop: 5,
              fontSize: 12,
              color: '#666',
            }}
          >
            {planOfCare
              .insuranceRuleDisplayName
              ? `Insurance Rule: ${planOfCare.insuranceRuleDisplayName}`
              : planOfCare
                  .insuranceRuleCode
                ? `Insurance Rule: ${planOfCare.insuranceRuleCode}`
                : 'Insurance rule determined by backend configuration.'}
          </div>
        </div>

        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color:
              saveStatus ===
              'error'
                ? '#b42318'
                : saveStatus ===
                    'invalid'
                  ? '#9a6700'
                  : saveStatus ===
                      'saving'
                    ? '#555'
                    : saveStatus ===
                        'preview'
                      ? '#9a6700'
                      : '#2e6a35',
          }}
        >
          {saveStatusText}
        </div>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            INFORMATION
            =================================================== */}
        <div
          style={{
            marginBottom: 10,
            color: '#64748b',
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          Physician certification is separate from the treating therapist's signature.
          Draft changes autosave; completion requires an explicit action and backend validation.
        </div>

        {isPreview && (
          <div
            style={{
              marginBottom: 15,
              border:
                '1px solid #d6a100',
              borderRadius: 8,
              background:
                '#fff8df',
              padding: 11,
              fontSize: 13,
            }}
          >
            This is the payer-configured
            Plan of Care preview. A
            database record will be
            created when documentation is
            first autosaved or when the
            Plan of Care is completed.
          </div>
        )}

        {/* ===================================================
            CERTIFICATION STATEMENT
            =================================================== */}
        <div className="clinical-poc-compact-panel">
          <button type="button" className="clinical-poc-compact-toggle"
            aria-expanded={showCertificationStatement || Boolean(fieldError('certificationText'))}
            onClick={() => setShowCertificationStatement((value) => !value)}>
            <span><strong>Certification Statement</strong>
              <span className="clinical-poc-compact-preview">{planOfCare.draftCertificationText?.trim() || 'Not entered'}</span>
            </span>
            <span aria-hidden="true">{showCertificationStatement || fieldError('certificationText') ? '−' : '+'}</span>
          </button>
          {(showCertificationStatement || Boolean(fieldError('certificationText'))) && (
            <div className="clinical-poc-compact-panel-body">
        <TextAreaField
          id="poc-certification-text"
          label="Certification Statement"
          value={
            planOfCare
              .draftCertificationText
          }
          disabled={disabled}
          required
          error={
            fieldError(
              'certificationText'
            )
          }
          rows={4}
          placeholder="Enter Plan of Care certification statement"
          onChange={(value) =>
            updateField(
              'draftCertificationText',
              value
            )
          }
        />
            </div>
          )}
        </div>

        {/* ===================================================
            REFERRING PROVIDER
            =================================================== */}
        <div
          style={{
            marginTop: 14,
          }}
        >
          <TextField
            id="poc-referring-provider"
            label="Referring Provider"
            value={
              planOfCare
                .draftReferringProviderName
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'referringProviderName'
              )
            }
            placeholder="Enter referring physician/provider name"
            onChange={(value) =>
              updateField(
                'draftReferringProviderName',
                value
              )
            }
          />

          {planOfCare
            .draftReferringProviderId && (
            <div
              style={{
                marginTop: 5,
                fontSize: 11,
                color: '#666',
              }}
            >
              Linked provider record #
              {
                planOfCare
                  .draftReferringProviderId
              }
            </div>
          )}
        </div>

        {/* ===================================================
            CERTIFICATION DATES
            =================================================== */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            marginTop: 14,
          }}
        >
          <DateField
            id="poc-certification-start-date"
            label="Certification Start Date"
            value={
              planOfCare
                .draftCertificationStartDate
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'certificationStartDate'
              )
            }
            onChange={(value) =>
              updateField(
                'draftCertificationStartDate',
                value
              )
            }
          />

          <DateField
            id="poc-certification-end-date"
            label="Certification End Date"
            value={
              planOfCare
                .draftCertificationEndDate
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'certificationEndDate'
              )
            }
            onChange={(value) =>
              updateField(
                'draftCertificationEndDate',
                value
              )
            }
          />
        </div>

        {/* ===================================================
            FREQUENCY / DURATION
            =================================================== */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
            marginTop: 14,
          }}
        >
          <TextField
            id="poc-frequency"
            label="Treatment Frequency"
            value={
              planOfCare
                .draftFrequencyText
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'frequencyText'
              )
            }
            placeholder="Example: 2-3 times per week"
            onChange={(value) =>
              updateField(
                'draftFrequencyText',
                value
              )
            }
          />

          <TextField
            id="poc-duration"
            label="Treatment Duration"
            value={
              planOfCare
                .draftDurationText
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'durationText'
              )
            }
            placeholder="Example: 6 weeks"
            onChange={(value) =>
              updateField(
                'draftDurationText',
                value
              )
            }
          />
        </div>

        {/* ===================================================
            PLAN NARRATIVE
            =================================================== */}
        <div className="clinical-poc-compact-panel">
          <button type="button" className="clinical-poc-compact-toggle"
            aria-expanded={showPlanNarrative || Boolean(fieldError('planText'))}
            onClick={() => setShowPlanNarrative((value) => !value)}>
            <span><strong>Plan of Care Narrative</strong>
              <span className="clinical-poc-compact-preview">{planOfCare.draftPlanText?.trim() || 'Not entered'}</span>
            </span>
            <span aria-hidden="true">{showPlanNarrative || fieldError('planText') ? '−' : '+'}</span>
          </button>
          {(showPlanNarrative || Boolean(fieldError('planText'))) && (
            <div className="clinical-poc-compact-panel-body">
          <TextAreaField
            id="poc-plan-text"
            label="Plan of Care Narrative"
            value={
              planOfCare
                .draftPlanText
            }
            disabled={disabled}
            required
            error={
              fieldError(
                'planText'
              )
            }
            rows={6}
            placeholder="Document the Plan of Care"
            onChange={(value) =>
              updateField(
                'draftPlanText',
                value
              )
            }
          />
            </div>
          )}
        </div>

        {/* ===================================================
            PHYSICIAN CERTIFICATION
            =================================================== */}
        <section
          style={{
            marginTop: 16,
            border:
              planOfCare
                .physicianSignatureRequired
                ? '1px solid #d6a100'
                : '1px solid #ddd',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding:
                '10px 12px',
              borderBottom:
                '1px solid #ddd',
              background:
                planOfCare
                  .physicianSignatureRequired
                  ? '#fff8df'
                  : '#fafafa',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flexWrap: 'wrap',
              }}
            >
              <strong>
                Referring Physician
                Certification
              </strong>

              {planOfCare
                .physicianSignatureRequired && (
                <span
                  style={{
                    borderRadius: 999,
                    padding: '2px 7px',
                    background: planOfCare.physicianSignatureComplete
                      ? '#e8f5e9'
                      : '#fce8e6',
                    color: planOfCare.physicianSignatureComplete
                      ? '#1f6b2a'
                      : '#8a1c13',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {planOfCare.physicianSignatureComplete
                    ? 'Required ✓'
                    : 'Required'}
                </span>
              )}

              {planOfCare
                .physicianSignatureComplete && (
                <span
                  style={{
                    borderRadius: 999,
                    padding:
                      '2px 7px',
                    background:
                      '#e7f6ea',
                    color: '#276738',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Certification entered
                </span>
              )}
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#666',
              }}
            >
              This is the referring
              physician/provider
              certification and is not
              the treating therapist's
              note signature.
            </div>
          </div>

          <button type="button" className="clinical-poc-physician-toggle"
            aria-expanded={showPhysicianCertification || Boolean(fieldError('physicianSignatureName') || fieldError('physicianSignatureDate'))}
            onClick={() => setShowPhysicianCertification((value) => !value)}>
            {showPhysicianCertification || fieldError('physicianSignatureName') || fieldError('physicianSignatureDate')
              ? 'Hide physician certification fields −' : 'Show physician certification fields +'}
          </button>
          {(showPhysicianCertification || Boolean(fieldError('physicianSignatureName') || fieldError('physicianSignatureDate'))) && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              padding: 12,
            }}
          >
            <TextField
              id="poc-physician-signature-name"
              label="Physician Certification Name"
              value={
                planOfCare
                  .draftPhysicianSignatureName
              }
              disabled={disabled}
              required={
                planOfCare
                  .physicianSignatureRequired
              }
              error={
                fieldError(
                  'physicianSignatureName'
                )
              }
              placeholder="Enter certifying physician name"
              onChange={(value) =>
                updateField(
                  'draftPhysicianSignatureName',
                  value
                )
              }
            />

            <DateField
              id="poc-physician-signature-date"
              label="Physician Certification Date"
              value={
                planOfCare
                  .draftPhysicianSignatureDate
              }
              disabled={disabled}
              required={
                planOfCare
                  .physicianSignatureRequired
              }
              error={
                fieldError(
                  'physicianSignatureDate'
                )
              }
              onChange={(value) =>
                updateField(
                  'draftPhysicianSignatureDate',
                  value
                )
              }
            />
          </div>
          )}
        </section>

        {/* ===================================================
            COMPLETION STATUS
            =================================================== */}
        <div
          style={{
            marginTop: 16,
            border:
              planOfCare
                .certificationComplete
                ? '1px solid #a8d5b0'
                : '1px solid #ddd',
            borderRadius: 8,
            background:
              planOfCare
                .certificationComplete
                ? '#f4fbf5'
                : '#fafafa',
            padding: 11,
          }}
        >
          <div
            style={{
              fontWeight: 700,
              marginBottom: 4,
            }}
          >
            Certification Status
          </div>

          <div
            style={{
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {planOfCare
              .certificationComplete
              ? 'All certification information required by the backend is currently complete.'
              : 'The Plan of Care is not yet complete.'}
          </div>

          {!planOfCare
            .certificationComplete &&
            incompleteFieldCount >
              0 && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: '#9a6700',
              }}
            >
              {incompleteFieldCount}{' '}
              completion{' '}
              {incompleteFieldCount ===
              1
                ? 'requirement remains'
                : 'requirements remain'}.
            </div>
          )}
        </div>

        {/* ===================================================
            COMPLETE ACTION
            =================================================== */}
        {!disabled && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 16,
            }}
          >
            <button
              type="button"
              disabled={
                saveStatus ===
                  'saving' ||
                isCompleted
              }
              onClick={
                completePlanOfCare
              }
            >
              {isCompleted
                ? 'Plan of Care Completed'
                : saveStatus ===
                    'saving'
                  ? 'Saving…'
                  : 'Complete Plan of Care'}
            </button>

            {!isCompleted && (
              <span
                style={{
                  fontSize: 12,
                  color: '#666',
                }}
              >
                Completion runs the
                strict backend
                certification
                validation.
              </span>
            )}
          </div>
        )}

        {/* ===================================================
            SAVE ERROR
            =================================================== */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 15,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Plan of Care was not
              saved.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
            >
              {saveError}
            </div>

            {!disabled && (
              <button
                type="button"
                onClick={
                  retrySave
                }
                style={{
                  marginTop: 8,
                }}
              >
                Retry Save
              </button>
            )}
          </div>
        )}

        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            Plan of Care documentation
            is read only because this
            clinical note is no longer
            in the normal Draft editing
            state.
          </div>
        )}
      </div>
    </section>
  );
}